// Libs
import classNames from 'classnames/bind';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid2, IconButton, Stack, Typography } from '@mui/material';
import { Controller, set, useFieldArray, useFormContext } from 'react-hook-form';
// Components, Layouts, Pages
import { BaseCheckbox, BaseDatePicker, BaseInput, BaseLabel, BaseSelect } from '~/components/common';
// Others
import { ICustomFee, IFormQuotes } from '~/utils/interface/quotes';
import {
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA,
  DOLLAR,
  EMPTY_STRING,
  optionsDepositMade,
  REG_EXP,
} from '~/utils/constants/common';
import { formatInputCurrency, generateUniqueId } from '~/utils/helper/common';
import { IBaseOption, IPayloadCallApiFn, IQueryParamBase, IResultCallApiFn } from '~/utils/interface/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getListCoupon } from '~/thunks/coupon/couponThunk';
import { DiscountTypeEnum } from '~/utils/enum/common';
import { IOptionCoupon } from '~/utils/interface/coupon';
// Styles, images, icons
import styles from './AdditionalChargesQuoteForm.module.scss';
import { icons } from '~/assets';

type Props = {
  couponNameDefault?: string;
  onRemoveCustom?: (id: string) => void;
};

const cx = classNames.bind(styles);

const AdditionalChargesQuoteForm = (props: Props) => {
  //#region Destructuring Props
  const { couponNameDefault, onRemoveCustom } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/quotes', 'common/base']);
  const dispatch = useAppDispatch();

  const { control, watch, setValue } = useFormContext<IFormQuotes>();

  const { append: appendCustomFees, remove: removeCustomFees } = useFieldArray({
    control,
    name: 'customFees',
  });

  const { customFees, isDamageWaiver } = watch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const fetchOptions = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionsDepositMade,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const handleAddCustomFees = () => {
    const newCustomFees: ICustomFee = {
      id: generateUniqueId(),
      customFeeAmount: EMPTY_STRING,
      customFeeName: EMPTY_STRING,
    };

    appendCustomFees(newCustomFees);
  };

  const handleRemoveCustomFees = (index: number) => {
    removeCustomFees(index);
  };

  const fetchOptionsCoupon = useCallback(
    async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IOptionCoupon>> => {
      const { page, searchKey } = payload;
      const payloadParams: IQueryParamBase = {
        page: page,
        limit: DEFAULT_LIMIT_PAGE,
        ...(searchKey && { textSearch: searchKey }),
      };

      try {
        const resp = await dispatch(getListCoupon(payloadParams)).unwrap();

        const { responses, pagination } = resp?.data;

        const checkData = responses.filter((item) => item.isValidCoupon);

        const formatOptions: IOptionCoupon[] = checkData.map((item) => ({
          label: `${item.name} - ${handleConvertNameCoupon(item.discountRate, item.discountType)}`,
          value: item.id,
          discountType: item.discountType,
          discountRate: item.discountRate,
        }));

        const hasMore: boolean = pagination?.page < pagination?.totalPages || false;

        return { optionsResp: formatOptions, hasMoreResp: hasMore };
      } catch (error) {
        return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
      }
    },
    []
  );

  const handleConvertNameCoupon = (discountRate: string, discountType: string) => {
    switch (discountType) {
      case DiscountTypeEnum.PERCENTAGE:
        return `${discountRate}%`;
      case DiscountTypeEnum.AMOUNT:
        return `$${discountRate}`;
      default:
        return EMPTY_STRING;
    }
  };
  //#endregion Handle Function

  return (
    <Stack id='additionalChargesQuoteFormComponent' className={cx('container')}>
      <BaseLabel styleType='title' label={t('form_quotes_title_additional_charges')} icon={icons.commonIconMenu} />

      <Stack spacing={'10px'}>
        <Grid2 container spacing={6}>
          <Grid2 size={4}>
            <Controller
              control={control}
              name={'deliveryCost'}
              render={({ field: { onChange, value } }) => (
                <BaseInput
                  label={t('form_quotes_label_delivery')}
                  placeholder={t('common_text_placeholder_currency', { ns: 'common/base' })}
                  startAdornment={<Typography className={cx('startAdornment')}>{DOLLAR}</Typography>}
                  value={value}
                  onChange={(event) => {
                    onChange(formatInputCurrency(event.target.value));
                  }}
                />
              )}
            />
          </Grid2>

          {/* <Grid2 size={4}>
            <Stack mt={'44px'}>
              <Controller
                control={control}
                name={'isDamageWaiver'}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseCheckbox
                      label={t('form_quotes_title_is_damage_waiver')}
                      checked={value}
                      onChange={(value) => {
                        onChange(value);
                        !value && setValue('damageWaiver', EMPTY_STRING);
                      }}
                    />
                  );
                }}
              />
            </Stack>
          </Grid2> */}

          {/* {isDamageWaiver && ( */}
          <Grid2 size={4}>
            <Controller
              control={control}
              name={'damageWaiver'}
              render={({ field: { onChange, value } }) => (
                <BaseInput
                  label={t('form_quotes_label_damage_waiver')}
                  placeholder={t('common_text_placeholder_currency', { ns: 'common/base' })}
                  startAdornment={<Typography className={cx('startAdornment')}>{DOLLAR}</Typography>}
                  value={value}
                  onChange={(event) => {
                    onChange(formatInputCurrency(event.target.value));
                  }}
                />
              )}
            />
          </Grid2>
          {/* )} */}
          <Grid2 size={4}>
            <Controller
              control={control}
              name={'laborFee'}
              render={({ field: { onChange, value } }) => (
                <BaseInput
                  label={t('form_quotes_label_labor_fee')}
                  placeholder={t('common_text_placeholder_currency', { ns: 'common/base' })}
                  startAdornment={<Typography className={cx('startAdornment')}>{DOLLAR}</Typography>}
                  value={value}
                  onChange={(event) => {
                    onChange(formatInputCurrency(event.target.value));
                  }}
                />
              )}
            />
          </Grid2>
        </Grid2>

        <Grid2 container spacing={6}>
          <Grid2 size={4}>
            <Controller
              control={control}
              name={'discountRate'}
              render={({ field: { onChange, value } }) => (
                <BaseInput
                  label={t('form_quotes_label_discount')}
                  placeholder={t('common_text_placeholder_currency', { ns: 'common/base' })}
                  startAdornment={<Typography className={cx('startAdornment')}>{DOLLAR}</Typography>}
                  value={value}
                  onChange={(event) => {
                    onChange(formatInputCurrency(event.target.value));
                  }}
                />
              )}
            />
          </Grid2>

          <Grid2 size={4}>
            <Controller
              control={control}
              name={'couponId'}
              render={({ field: { onChange, value } }) => (
                <BaseSelect
                  isSearch
                  type='loadMore'
                  gap={8}
                  styleType='outlined'
                  label={t('form_quotes_label_coupon')}
                  callApiFn={fetchOptionsCoupon}
                  value={value}
                  itemNameDefault={couponNameDefault}
                  onChange={(option) => {
                    onChange(option?.value);
                    setValue('couponType', option?.discountType);
                    setValue('couponRate', option?.discountRate);
                  }}
                />
              )}
            />
          </Grid2>

          <Grid2 size={4}>
            <Controller
              control={control}
              name={'poNumber'}
              render={({ field: { onChange, value } }) => (
                <BaseInput
                  label={t('form_quotes_label_po_number')}
                  placeholder={t('form_quotes_placeholder_po_number')}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          </Grid2>
        </Grid2>

        <Grid2 container spacing={6}>
          <Grid2 size={4}>
            <Controller
              control={control}
              name={'depositMade'}
              render={({ field: { onChange, value } }) => (
                <BaseSelect
                  gap={8}
                  styleType='outlined'
                  label={t('form_quotes_label_deposit')}
                  callApiFn={fetchOptions}
                  value={value}
                  onChange={(option) => {
                    onChange(option?.value);
                  }}
                />
              )}
            />
          </Grid2>

          <Grid2 size={4}>
            <Controller
              control={control}
              name='depositDueDate'
              render={({ field: { onChange, value } }) => (
                <BaseDatePicker label={t('form_quotes_title_deposit_due_date')} value={value} onChange={onChange} />
              )}
            />
          </Grid2>

          <Grid2 size={4}>
            <Controller
              control={control}
              name={'taxRate'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    label={t('form_quotes_title_tax_rate')}
                    placeholder={t('common_text_placeholder_number', { ns: 'common/base' })}
                    startAdornment={<Typography className={cx('startAdornment')}>{'%'}</Typography>}
                    value={String(value)}
                    onChange={(e) => {
                      const inputValue = e.target.value.replace(REG_EXP.NUMBER, EMPTY_STRING);
                      const numericValue = inputValue ? Number(inputValue) : EMPTY_STRING;
                      onChange(String(numericValue));
                    }}
                  />
                );
              }}
            />
          </Grid2>
        </Grid2>

        <Grid2 container spacing={6}>
          <Grid2 size={4}>
            <Controller
              control={control}
              name={'mileage'}
              render={({ field: { onChange, value } }) => (
                <BaseInput
                  label={t('form_quotes_label_mileage')}
                  placeholder={t('common_text_placeholder_currency', { ns: 'common/base' })}
                  value={value}
                  onChange={(event) => {
                    onChange(formatInputCurrency(event.target.value));
                  }}
                />
              )}
            />
          </Grid2>
        </Grid2>
      </Stack>

      <Stack spacing={4} marginTop={4}>
        <BaseLabel styleType='subTitle' label={t('form_quotes_title_custom_fees')} />

        <Stack className={cx('customFeesContainer')} spacing={4}>
          {customFees &&
            customFees?.map((item, index) => {
              const filedName = `customFees.${index}` as const;

              return (
                <Box key={index} className={cx('customFeesItem')}>
                  <Box className={cx('feeNameView')}>
                    <Controller
                      key={item.id}
                      control={control}
                      name={`${filedName}.customFeeName`}
                      render={({ field: { onChange, value } }) => (
                        <BaseInput
                          label={t('form_quotes_label_fee_name')}
                          placeholder={t('form_quotes_placeholder_fee_name')}
                          value={value}
                          onChange={onChange}
                        />
                      )}
                    />
                  </Box>

                  <Box className={cx('feeAmountView')}>
                    <Controller
                      key={item.id}
                      control={control}
                      name={`${filedName}.customFeeAmount`}
                      render={({ field: { onChange, value } }) => (
                        <BaseInput
                          label={t('form_quotes_label_fee_amount')}
                          placeholder={t('common_text_placeholder_currency', { ns: 'common/base' })}
                          value={value}
                          startAdornment={<Typography className={cx('startAdornment')}>{DOLLAR}</Typography>}
                          onChange={(event) => {
                            onChange(formatInputCurrency(event.target.value));
                          }}
                        />
                      )}
                    />
                  </Box>

                  <IconButton
                    key={item.id}
                    className={cx('buttonRemove')}
                    onClick={() => {
                      handleRemoveCustomFees(index);
                      onRemoveCustom?.(item?.id || EMPTY_STRING);
                    }}
                  >
                    <img src={icons.commonIconTrash} alt={t('common_alt_img')} />
                  </IconButton>
                </Box>
              );
            })}
        </Stack>

        <Typography component={'span'} className={cx('buttonAdd')} onClick={handleAddCustomFees}>
          {t('common_button_add_item', { ns: 'common/base' })}
        </Typography>
      </Stack>
    </Stack>
  );
};

export default AdditionalChargesQuoteForm;
